import {
  ReactNode,
  createContext,
  useEffect,
  useState,
  useContext,
} from 'react';

import { UserRole } from '../../../../shared/types';

import isAdminUser from '../../helpers/isAdminUser';

import { Nullable } from '../../../../shared/types';
import { useMsal } from '@azure/msal-react';
import _ from 'lodash';
import { getMsalInstance } from '../../auth';

export type UserContextType = {
  userEmail: Nullable<string>;
  userId: Nullable<number>;
  setUserEmail: React.Dispatch<
    React.SetStateAction<Nullable<string>>
  >;
  setUserFirstName: React.Dispatch<
    React.SetStateAction<Nullable<string>>
  >;
  setUserLastName: React.Dispatch<
    React.SetStateAction<Nullable<string>>
  >;
  setUserId: React.Dispatch<React.SetStateAction<Nullable<number>>>;
  loggedIn: boolean;
  setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  userRoles: UserRole[] | undefined;
  isAdmin: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setUserPhoneNumber: React.Dispatch<
    React.SetStateAction<Nullable<string>>
  >;
  userFirstName: Nullable<string>;
  userLastName: Nullable<string>;
  userPhoneNumber: Nullable<string>;
  isAuthenticated: boolean;
  isLoadingUser: boolean;
};

const UserContext = createContext<UserContextType>(null!);

const UserProvider = (props: { children: ReactNode }) => {
  const [userEmail, setUserEmail] =
    useState<Nullable<string>>(undefined);
  const [userId, setUserId] = useState<Nullable<number>>(undefined);
  const [userFirstName, setUserFirstName] =
    useState<Nullable<string>>(undefined);
  const [userLastName, setUserLastName] =
    useState<Nullable<string>>(undefined);
  const [userPhoneNumber, setUserPhoneNumber] =
    useState<Nullable<string>>(undefined);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<UserRole[] | undefined>(
    undefined,
  );
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const [hasUnsavedChanges, setHasUnsavedChanges] =
    useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isAdmin = userRoles ? isAdminUser(userRoles) : false;

  const children = props.children;

  const { accounts, inProgress } = useMsal();
  useEffect(() => {
    const checkAuth = async () => {
      if (inProgress !== 'none') {
        // MSAL is still processing a redirect or token
        setIsLoadingUser(true);
        return;
      }
      setIsLoadingUser(true);
      if (accounts && accounts[0]) {
        const account = accounts[0];
        const [firstName, lastName] = account?.name?.split(' ') ?? '';
        const email = account.username.toLowerCase();

        setUserFirstName(firstName);
        setUserLastName(lastName);
        setUserEmail(email);

        try {
          const msalInstance = getMsalInstance();

          const a = msalInstance.getAllAccounts()[0];
          const response: any = await msalInstance.acquireTokenSilent(
            {
              account: a,
              scopes: ['openid', 'profile'],
            },
          );

          const idToken = response.idTokenClaims;
          setUserRoles([idToken['extension_PricingPlannerRole']]);
          setUserPhoneNumber(idToken['extension_PhoneNumber']);
          setIsAuthenticated(true);
        } catch (err) {
          setIsAuthenticated(false);
        } finally {
          setIsLoadingUser(false);
        }
      } else {
        setIsAuthenticated(false);
        setIsLoadingUser(false);
      }
    };

    checkAuth();
  }, [accounts, inProgress]);

  return (
    <UserContext.Provider
      value={{
        userEmail,
        userId,
        setUserEmail,
        setUserFirstName,
        setUserLastName,
        setUserPhoneNumber,
        userPhoneNumber,
        setUserId,
        loggedIn,
        setLoggedIn,
        userRoles,
        isAdmin,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        userFirstName,
        userLastName,
        isAuthenticated,
        isLoadingUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUserContext = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error(
      'useUserContext was used outside of its Provider',
    );
  }

  return context;
};

export default useUserContext;

export { UserContext, UserProvider };
